import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import useDebounce from '@/components/hooks/useDebounce';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { cn } from '@/lib/utils';
import {
  AccountOption,
  accountsQueryOptions,
  useAccountByIdQuery,
} from '@/services/accounts';
import { useAccessToken } from '@/services/auth';

type AccountsComboboxProps = {
  value?: string;
  onChange?: (accountId: string | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  container?: HTMLElement | null;
};

export const AccountsCombobox = ({
  value,
  onChange,
  placeholder,
  className,
  disabled,
  container,
}: AccountsComboboxProps) => {
  const { t } = useTranslation();
  const [accountSearch, setAccountSearch] = useState('');
  const debouncedSearch = useDebounce(accountSearch);
  const token = useAccessToken();

  const { data: accounts } = useQuery({
    ...accountsQueryOptions(token, {
      search: debouncedSearch,
    }),
    enabled: !!token && (!value || debouncedSearch.length > 0),
  });
  const { data: initialAccount } = useAccountByIdQuery(value);

  const selectedAccount = value
    ? (accounts?.find((a) => a.id === value) ?? initialAccount ?? null)
    : null;

  useEffect(() => {
    setAccountSearch(
      selectedAccount
        ? `${selectedAccount.code} - ${selectedAccount.name}`
        : '',
    );
  }, [selectedAccount]);

  const handleAccountChange = (account: AccountOption | null) => {
    onChange?.(account?.id);
  };

  return (
    <Combobox
      items={accounts}
      value={selectedAccount}
      onValueChange={handleAccountChange}
      itemToStringValue={(option) => option.id}
      itemToStringLabel={(option) => `${option.code} - ${option.name}`}
    >
      <ComboboxInput
        placeholder={placeholder ?? t('account.selectAccount')}
        className={cn('w-82', className)}
        value={accountSearch}
        onChange={(e) => {
          setAccountSearch(e.target.value);
        }}
        disabled={disabled}
        showClear
      />
      <ComboboxContent container={container}>
        <ComboboxEmpty>{t('account.noAccountsFound')}</ComboboxEmpty>
        <ComboboxList>
          {(account: AccountOption) => (
            <ComboboxItem key={account.id} value={account}>
              {account.code} - {account.name}
            </ComboboxItem>
          )}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
};
